import { useSnackbar } from 'notistack'
import { useContext, useState } from 'react'
import { FieldErrors } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { mapGraphQLErrors } from 'providers/tanstack-query/errors'
import { useMutation } from 'providers/tanstack-query/useMutation'
import { UserWorkspacesContext } from '../../contexts/UserWorkspacesContext'
import { RemoveSchema } from '../../schemas'
import { Data, REMOVE_PUBLISHER_FROM_WORKSPACE, Variables } from './api'
import { UseRemovePublisherFromWorkspace, UseRemovePublisherFromWorkspaceProps } from './types'

export const useRemovePublisherFromWorkspace = ({
  workspaceId,
}: UseRemovePublisherFromWorkspaceProps): UseRemovePublisherFromWorkspace => {
  const { t } = useTranslation('features/user')
  const { enqueueSnackbar } = useSnackbar()
  const [errors, setErrors] = useState<FieldErrors<RemoveSchema>>({})
  const { remove } = useContext(UserWorkspacesContext)

  const { mutateAsync } = useMutation<{ input: Variables }, Data>({
    mutation: REMOVE_PUBLISHER_FROM_WORKSPACE,
    onCompleted: async (data: Data) => {
      const {
        removePublisherFromWorkspace: { errors },
      } = data

      if (errors && errors.length > 0) {
        setErrors(mapGraphQLErrors(errors))
        enqueueSnackbar(t('form.publisher.remove.error'), { variant: 'error' })
      } else {
        setErrors({})
        enqueueSnackbar(t('form.publisher.remove.success'), {
          variant: 'success',
        })

        remove(workspaceId)
      }
    },
  })

  const removePublisherFromWorkspace = ({ userId, workspaceId }: RemoveSchema): void =>
    void mutateAsync({
      input: {
        userId,
        workspaceId: workspaceId.value,
      },
    })

  return { errors, removePublisherFromWorkspace }
}
